/**
 * EmptyState — shared "you don't have any {thing} yet" component.
 *
 * Phase 84.23.1 (Activation Suite v2 — empty states).
 *
 * Before: every list page had its own one-liner ("No goals yet.",
 * "No recurring commitments yet.") — inconsistent tone, no
 * clear next step, no contextual illustration. Users hit a blank
 * page and bounced.
 *
 * After: every list page renders <EmptyState /> with an icon +
 * headline + one-sentence context + primary CTA (+ optional
 * secondary CTA). Users always know exactly what to do next.
 *
 * Design choices:
 *   • Illustration = single lucide icon in a soft sage tint circle.
 *     No SVG art (won't scale to 40+ locales).
 *   • Headline = imperative-mood ("Start your first goal") not
 *     stative ("You have no goals"). Draws action.
 *   • Body = one sentence, ≤ 16 words. Explains WHY without a wall.
 *   • Primary CTA = the single action we most want them to take.
 *   • Secondary CTA = "Explore Demo Account" for users who can't
 *     act (no accounts / no transactions).
 *
 * Consumers wire it in one line per list page.
 */
import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

export default function EmptyState({
  icon: Icon,
  title,
  body,
  primaryCta,
  secondaryCta,
  testid,
  className = '',
}) {
  return (
    <Card
      className={`rounded-2xl border-dashed border-border/70 ${className}`}
      data-testid={testid || 'empty-state'}
    >
      <CardContent className="p-10 text-center max-w-md mx-auto">
        {Icon ? (
          <div
            className="mx-auto mb-4 h-14 w-14 rounded-full bg-sage-light/50 grid place-items-center text-evergreen"
            aria-hidden="true"
          >
            <Icon className="h-6 w-6" />
          </div>
        ) : null}
        <h3 className="font-display text-lg font-semibold text-charcoal mb-2">
          {title}
        </h3>
        <p className="text-sm text-muted-foreground leading-relaxed mb-6">
          {body}
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
          {primaryCta ? (
            <Button
              type="button"
              onClick={primaryCta.onClick}
              data-testid={primaryCta.testid || 'empty-state-primary'}
              size="sm"
            >
              {primaryCta.label}
            </Button>
          ) : null}
          {secondaryCta ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={secondaryCta.onClick}
              data-testid={secondaryCta.testid || 'empty-state-secondary'}
            >
              {secondaryCta.label}
            </Button>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
